import React, { useCallback, useEffect, useState } from 'react'
import { Calendar, Edit, KeyRound, Mail, Plus, RotateCcw, Search, ShieldCheck, Trash2, UserCheck, UserX } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import { DEFAULT_LOCAL_PASSWORD, clearLocalPasswordOverride, createLocalCredentials, normalizeEmail, readSession } from '../lib/appSession'

interface AdminUser {
  _id?: string
  id?: string
  name: string
  email: string
  isActive: boolean
  role?: string
  authProvider?: 'local' | 'google'
  googleLinked?: boolean
  passwordHash?: string
  passwordSalt?: string
  mustResetPassword?: boolean
  passwordUpdatedAt?: string
  createdAt: string
  updatedAt?: string
  recordVersion?: number
  adminRootId?: string
  supersedesAdminId?: string
  lifecycleAction?: 'create' | 'update' | 'status' | 'reset_password' | 'delete' | 'migrate'
  isDeleted?: boolean
  deletedAt?: string
}

type AdminLifecycleAction = NonNullable<AdminUser['lifecycleAction']>

const getAdminId = (admin: AdminUser) => String(admin._id ?? admin.id ?? '').trim()

const isPermissionError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes('Insufficient permissions') || message.includes('OPERATION_FAILED')
}

const getAdminRecordTime = (admin: AdminUser) => {
  const candidates = [admin.deletedAt, admin.updatedAt, admin.passwordUpdatedAt, admin.createdAt]
  return candidates.reduce((latest, value) => {
    const timestamp = value ? new Date(value).getTime() : 0
    return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest
  }, 0)
}

const compareAdminVersions = (left: AdminUser, right: AdminUser) => {
  const versionDifference = Number(right.recordVersion || 1) - Number(left.recordVersion || 1)
  if (versionDifference !== 0) return versionDifference
  return getAdminRecordTime(right) - getAdminRecordTime(left)
}

const collapseAdminHistory = (records: AdminUser[]) => {
  const grouped = new Map<string, AdminUser[]>()

  records.forEach((admin) => {
    const emailKey = normalizeEmail(admin.email || '')
    const fallbackKey = getAdminId(admin)
    const key = admin.adminRootId || fallbackKey || emailKey
    if (!key) return
    grouped.set(key, [...(grouped.get(key) || []), admin])
  })

  return Array.from(grouped.values())
    .map((versions) => [...versions].sort(compareAdminVersions)[0])
    .filter((admin) => admin.isDeleted !== true)
    .sort(compareAdminVersions)
}

const mergeAdminUpdate = (original: AdminUser, updated: AdminUser): AdminUser => ({
  ...original,
  ...updated,
  _id: updated._id ?? original._id,
  id: updated.id ?? original.id,
})

const AdminManager: React.FC = () => {
  const session = readSession()
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [adminsPage, setAdminsPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null)
  const [pendingAction, setPendingAction] = useState<{
    type: 'reset_password' | 'delete'
    admin: AdminUser
  } | null>(null)
  const [processingAction, setProcessingAction] = useState(false)

  const createAdminReplacement = useCallback(async (
    admin: AdminUser,
    updates: Partial<AdminUser>,
    lifecycleAction: AdminLifecycleAction
  ): Promise<AdminUser> => {
    const now = new Date().toISOString()
    const previousId = getAdminId(admin)
    const replacementData = {
      name: updates.name ?? admin.name,
      email: normalizeEmail(updates.email ?? admin.email),
      isActive: updates.isActive ?? admin.isActive,
      role: updates.role ?? admin.role ?? 'admin',
      authProvider: updates.authProvider ?? admin.authProvider ?? 'local',
      googleLinked: updates.googleLinked ?? admin.googleLinked ?? false,
      passwordHash: updates.passwordHash ?? admin.passwordHash,
      passwordSalt: updates.passwordSalt ?? admin.passwordSalt,
      mustResetPassword: updates.mustResetPassword ?? admin.mustResetPassword ?? false,
      passwordUpdatedAt: updates.passwordUpdatedAt ?? admin.passwordUpdatedAt,
      recordVersion: Number(admin.recordVersion || 1) + 1,
      adminRootId: admin.adminRootId || previousId || undefined,
      supersedesAdminId: previousId || undefined,
      lifecycleAction,
      isDeleted: updates.isDeleted === true,
      deletedAt: updates.isDeleted === true ? updates.deletedAt || now : undefined,
      createdAt: now,
      updatedAt: now,
    }

    return await lumi.entities.admins.create(replacementData) as AdminUser
  }, [])

  const ensureLocalAdminAccess = useCallback(async (admin: AdminUser): Promise<AdminUser> => {
    const needsPassword = !admin.passwordHash || !admin.passwordSalt
    const needsUnlink = admin.authProvider !== 'local' || admin.googleLinked !== false

    if (!needsPassword && !needsUnlink) {
      return admin
    }

    const credentials = needsPassword
      ? await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, { mustResetPassword: true })
      : {}

    const adminId = getAdminId(admin)
    const updates = {
      ...credentials,
      authProvider: 'local' as const,
      googleLinked: false,
      updatedAt: new Date().toISOString(),
    }

    if (!adminId) {
      return admin
    }

    try {
      const updatedAdmin = await lumi.entities.admins.update(adminId, updates) as AdminUser
      return mergeAdminUpdate(admin, updatedAdmin)
    } catch (error) {
      if (!isPermissionError(error)) throw error
      console.warn('[APP DNA] Update de administrador bloqueado. Criando versão persistente de substituição.', error)
      return createAdminReplacement(admin, updates, 'migrate')
    }
  }, [createAdminReplacement])

  const loadAdmins = useCallback(async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.admins.list({
        sort: { createdAt: -1 },
      })
      const list = collapseAdminHistory((response.list || []) as AdminUser[])
      const normalizedAdmins = await Promise.all(list.map(ensureLocalAdminAccess))
      setAdmins(normalizedAdmins)
    } catch (error) {
      console.error('Erro ao carregar administradores:', error)
      toast.error('Erro ao carregar administradores')
    } finally {
      setLoading(false)
    }
  }, [ensureLocalAdminAccess])

  useEffect(() => {
    loadAdmins()
  }, [loadAdmins])

  const handleCreateAdmin = async (adminData: Partial<AdminUser>) => {
    try {
      const credentials = await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, {
        mustResetPassword: true,
      })
      const newAdmin = await lumi.entities.admins.create({
        ...adminData,
        email: normalizeEmail(adminData.email || ''),
        role: 'admin',
        ...credentials,
        isActive: true,
        recordVersion: 1,
        lifecycleAction: 'create',
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as AdminUser

      setAdmins((prev) => [newAdmin, ...prev])
      setShowCreateModal(false)
      toast.success(`Administrador cadastrado. Senha inicial: ${DEFAULT_LOCAL_PASSWORD}`)
    } catch (error) {
      console.error('Erro ao criar administrador:', error)
      toast.error('Erro ao criar administrador')
    }
  }

  const handleUpdateAdmin = async (adminId: string, updates: Partial<AdminUser>) => {
    try {
      const currentAdmin = admins.find((admin) => getAdminId(admin) === adminId)
      if (!currentAdmin) {
        toast.error('Administrador não encontrado para atualização.')
        return
      }

      const normalizedUpdates = {
        ...updates,
        ...(typeof updates.email === 'string' ? { email: normalizeEmail(updates.email) } : {}),
      }

      const updatePayload = {
        ...normalizedUpdates,
        updatedAt: new Date().toISOString(),
      }

      let updatedAdmin: AdminUser
      try {
        const remoteAdmin = await lumi.entities.admins.update(adminId, updatePayload) as AdminUser
        updatedAdmin = mergeAdminUpdate(currentAdmin, remoteAdmin)
      } catch (error) {
        if (!isPermissionError(error)) throw error
        console.warn('[APP DNA] Update de administrador bloqueado. Criando versão persistente de substituição.', error)
        updatedAdmin = await createAdminReplacement(currentAdmin, updatePayload, updates.isActive === undefined ? 'update' : 'status')
      }

      setAdmins((prev) => prev.map((admin) => getAdminId(admin) === adminId ? updatedAdmin : admin))
      setEditingAdmin(null)
      toast.success('Administrador atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar administrador:', error)
      toast.error(`Erro ao atualizar administrador: ${error instanceof Error ? error.message : 'erro desconhecido'}`)
    }
  }

  const handleToggleStatus = async (admin: AdminUser) => {
    const isCurrentAdmin = session?.profile === 'admin' && normalizeEmail(admin.email) === normalizeEmail(session.email)
    if (isCurrentAdmin && admin.isActive) {
      toast.error('Você não pode desativar o próprio acesso.')
      return
    }

    const adminId = getAdminId(admin)
    if (!adminId) {
      toast.error('Administrador sem identificador válido.')
      return
    }

    await handleUpdateAdmin(adminId, { isActive: !admin.isActive })
  }

  const handleResetPassword = async (admin: AdminUser) => {
    try {
      setProcessingAction(true)
      const adminId = getAdminId(admin)
      if (!adminId) throw new Error('Administrador sem identificador válido.')

      const credentials = await createLocalCredentials(DEFAULT_LOCAL_PASSWORD, {
        mustResetPassword: true,
      })
      const updatePayload = {
        ...credentials,
        updatedAt: new Date().toISOString(),
      }

      let updatedAdmin: AdminUser
      try {
        const remoteAdmin = await lumi.entities.admins.update(adminId, updatePayload) as AdminUser
        updatedAdmin = mergeAdminUpdate(admin, remoteAdmin)
      } catch (error) {
        if (!isPermissionError(error)) throw error
        console.warn('[APP DNA] Reset de senha bloqueado no update. Criando versão persistente de substituição.', error)
        updatedAdmin = await createAdminReplacement(admin, updatePayload, 'reset_password')
      }

      clearLocalPasswordOverride('admin', admin.email)
      setAdmins((prev) => prev.map((item) => getAdminId(item) === adminId ? updatedAdmin : item))
      setPendingAction(null)
      toast.success(`Senha redefinida para ${DEFAULT_LOCAL_PASSWORD}.`)
    } catch (error) {
      console.error('Erro ao redefinir senha:', error)
      toast.error(`Erro ao redefinir senha: ${error instanceof Error ? error.message : 'erro desconhecido'}`)
    } finally {
      setProcessingAction(false)
    }
  }

  const handleDeleteAdmin = async (admin: AdminUser) => {
    const isCurrentAdmin = session?.profile === 'admin' && normalizeEmail(admin.email) === normalizeEmail(session.email)
    if (isCurrentAdmin) {
      toast.error('Você não pode excluir o próprio acesso.')
      setPendingAction(null)
      return
    }

    try {
      setProcessingAction(true)
      const adminId = getAdminId(admin)
      if (!adminId) throw new Error('Administrador sem identificador válido.')

      await createAdminReplacement(admin, {
        isActive: false,
        isDeleted: true,
        deletedAt: new Date().toISOString(),
      }, 'delete')

      clearLocalPasswordOverride('admin', admin.email)
      setAdmins((prev) => prev.filter((item) => getAdminId(item) !== adminId))
      setPendingAction(null)
      toast.success('Administrador excluído com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir administrador:', error)
      toast.error(`Erro ao excluir administrador: ${error instanceof Error ? error.message : 'erro desconhecido'}`)
    } finally {
      setProcessingAction(false)
    }
  }

  const filteredAdmins = admins.filter((admin) => {
    const query = searchTerm.toLowerCase()
    const matchesSearch =
      admin.name.toLowerCase().includes(query) ||
      admin.email.toLowerCase().includes(query)
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && admin.isActive) ||
      (statusFilter === 'inactive' && !admin.isActive)

    return matchesSearch && matchesStatus
  })

  const adminsTotalPages = Math.max(1, Math.ceil(filteredAdmins.length / 10))
  const adminsCurrentPage = Math.min(adminsPage, adminsTotalPages)
  const adminsPageItems = filteredAdmins.slice((adminsCurrentPage - 1) * 10, adminsCurrentPage * 10)

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Administradores</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
        >
          <Plus size={20} />
          <span>Novo Administrador</span>
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar administrador..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos os Status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {filteredAdmins.length === 0 ? (
          <div className="col-span-full rounded-lg border border-gray-200 bg-white py-12 text-center shadow-sm">
            <ShieldCheck size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              {searchTerm || statusFilter !== 'all' ? 'Nenhum administrador encontrado' : 'Nenhum administrador cadastrado'}
            </h3>
            <p className="mb-4 text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'Tente ajustar os filtros de busca'
                : 'Cadastre administradores adicionais para operar o painel'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
              >
                Cadastrar Administrador
              </button>
            )}
          </div>
        ) : (
          adminsPageItems.map((admin) => (
            <div key={getAdminId(admin) || admin.email} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 truncate text-lg font-semibold text-gray-900">{admin.name}</h3>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                      admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {admin.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                      admin.mustResetPassword ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      <KeyRound size={12} />
                      {admin.mustResetPassword ? 'Senha padrão' : 'Senha definida'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-4 space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Mail size={16} />
                  <span className="truncate">{admin.email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>Desde {new Date(admin.createdAt).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setEditingAdmin(admin)}
                  className="flex items-center space-x-1 rounded-lg px-3 py-1 text-sm text-blue-600 transition-colors hover:bg-blue-50"
                >
                  <Edit size={16} />
                  <span>Editar</span>
                </button>

                <button
                  onClick={() => handleToggleStatus(admin)}
                  className={`flex items-center space-x-1 rounded-lg px-3 py-1 text-sm transition-colors ${
                    admin.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                  }`}
                >
                  {admin.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                  <span>{admin.isActive ? 'Desativar' : 'Ativar'}</span>
                </button>

                <button
                  onClick={() => setPendingAction({ type: 'reset_password', admin })}
                  className="flex items-center space-x-1 rounded-lg px-3 py-1 text-sm text-amber-700 transition-colors hover:bg-amber-50"
                >
                  <RotateCcw size={16} />
                  <span>Resetar senha</span>
                </button>

                <button
                  onClick={() => {
                    const isCurrentAdmin = session?.profile === 'admin' && normalizeEmail(admin.email) === normalizeEmail(session.email)
                    if (isCurrentAdmin) {
                      toast.error('Você não pode excluir o próprio acesso.')
                      return
                    }
                    setPendingAction({ type: 'delete', admin })
                  }}
                  className="flex items-center space-x-1 rounded-lg px-3 py-1 text-sm text-red-600 transition-colors hover:bg-red-50"
                >
                  <Trash2 size={16} />
                  <span>Excluir</span>
                </button>
              </div>
            </div>
          ))
        )}
        {filteredAdmins.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 sm:flex-row">
            <span>
              Pagina {adminsCurrentPage} de {adminsTotalPages} · {filteredAdmins.length} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={adminsCurrentPage <= 1}
                onClick={() => setAdminsPage(adminsCurrentPage - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={adminsCurrentPage >= adminsTotalPages}
                onClick={() => setAdminsPage(adminsCurrentPage + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>

      {(showCreateModal || editingAdmin) && (
        <AdminFormModal
          admin={editingAdmin}
          onSave={editingAdmin
            ? (data) => {
                const adminId = getAdminId(editingAdmin)
                if (!adminId) {
                  toast.error('Administrador sem identificador válido.')
                  return
                }
                void handleUpdateAdmin(adminId, data)
              }
            : handleCreateAdmin}
          onClose={() => {
            setShowCreateModal(false)
            setEditingAdmin(null)
          }}
        />
      )}

      {pendingAction && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className={`mb-4 inline-flex rounded-full p-3 ${
              pendingAction.type === 'delete'
                ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {pendingAction.type === 'delete' ? <Trash2 size={24} /> : <RotateCcw size={24} />}
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {pendingAction.type === 'delete' ? 'Excluir administrador' : 'Redefinir senha'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {pendingAction.type === 'delete'
                ? `Confirma a exclusão do acesso de ${pendingAction.admin.name}?`
                : `A senha de ${pendingAction.admin.name} será redefinida para ${DEFAULT_LOCAL_PASSWORD} e deverá ser alterada no próximo acesso.`}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={processingAction}
                onClick={() => setPendingAction(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={processingAction}
                onClick={() => {
                  if (pendingAction.type === 'delete') {
                    void handleDeleteAdmin(pendingAction.admin)
                  } else {
                    void handleResetPassword(pendingAction.admin)
                  }
                }}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                  pendingAction.type === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                {processingAction
                  ? 'Processando...'
                  : pendingAction.type === 'delete'
                    ? 'Confirmar exclusão'
                    : 'Confirmar redefinição'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const AdminFormModal: React.FC<{
  admin?: AdminUser | null
  onSave: (data: Partial<AdminUser>) => void
  onClose: () => void
}> = ({ admin, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: admin?.name || '',
    email: admin?.email || '',
    isActive: admin?.isActive ?? true,
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    onSave({
      name: formData.name,
      email: formData.email,
      isActive: formData.isActive,
      role: 'admin',
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white">
        <div className="p-6">
          <h2 className="mb-6 text-2xl font-bold text-gray-900">
            {admin ? 'Editar Administrador' : 'Novo Administrador'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Nome Completo *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o nome completo"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                E-mail *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o e-mail"
              />
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
              <div className="flex items-start gap-3">
                <KeyRound size={18} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Acesso administrativo</p>
                  <p className="mt-1">
                    Novos administradores recebem a senha padrão {DEFAULT_LOCAL_PASSWORD} e devem redefinir a senha no primeiro acesso.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="adminIsActive"
                checked={formData.isActive}
                onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="adminIsActive" className="ml-2 text-sm text-gray-700">
                Administrador ativo
              </label>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-6 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
              >
                {admin ? 'Atualizar' : 'Cadastrar'} Administrador
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AdminManager